import { titleCase } from '../../utils';
import { CashPricePharmacyOptions, CashPricePharmacySuggestion, type Task } from './types';
import type { TaskSummary } from '../../api';
import { formatStandardDate, formatStandardDateLocaleString } from 'shared-utils/utils/formatDate';

const VALID_BENEFIT_TYPES = ['pharmacy', 'medical'] as const;

/**
 * Format task data for display in the UI.
 *
 * This transforms date fields from ISO format to human-readable formats so that:
 * 1. Dates display correctly in table cells
 * 2. Client-side search/filtering works on the formatted date strings (e.g., "01/15/2024")
 *
 * Must be applied after fetching tasks from the API and before passing to TasksTable.
 */
export const formatTaskData = (tasks: TaskSummary[]) =>
  tasks.map((item: TaskSummary) => ({
    ...item,
    last_comment_created_at: formatStandardDateLocaleString(item.last_comment_created_at),
    patient_dob: item.patient_dob ? formatStandardDate(item.patient_dob) : null,
    drug: item.drug
      ? {
          ...item.drug,
          prescription_recieved_at: formatStandardDateLocaleString(item.drug.prescription_recieved_at),
        }
      : null,
  }));

/**
 * Try to parse a search term as a date and format it as MM/DD/YYYY.
 * Returns null if the term is too short (<6 chars) or cannot be parsed.
 *
 * This mirrors the backend's try_parse_search_date in python/partner_portal/helpers.py.
 * Keep these implementations in sync when modifying date search behavior.
 */
export const tryParseSearchDateToMMDDYYYY = (searchTerm: string): string | null => {
  if (searchTerm.length < 6) return null;
  const parsed = new Date(searchTerm);
  if (isNaN(parsed.getTime())) return null;
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  const year = parsed.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * Returns a formatted label for the benefit type if it should be displayed.
 * Only returns a value for known benefit types (pharmacy, medical).
 */
export const getBenefitTypeLabel = (task: Task): string | null => {
  if (!task.benefit_type) {
    return null;
  }

  if (!VALID_BENEFIT_TYPES.includes(task.benefit_type as any)) {
    return null;
  }

  return `${titleCase(task.benefit_type)} benefits`;
};

/**
 * Get the cheapest and preferred cash price pharmacies from the cash price pharmacy suggestions list.
 *
 * If the cheapest and preferred pharmacies are the same, only one pharmacy option is returned as the cheapest pharmacy.
 * If there are multiple suggestions, the preferred pharmacy should not be the same as the cheapest pharmacy.
 */
export const getPharmacyOptionsFromSuggestionsList = (
  cashPricePharmacySuggestions: CashPricePharmacySuggestion[],
): CashPricePharmacyOptions => {
  const cheapestPharmacy = cashPricePharmacySuggestions
    .filter((suggestion) => suggestion.price !== null)
    .reduce(
      (cheapest, current) => {
        if (!cheapest || current.price < cheapest.price!) {
          return current;
        }
        return cheapest;
      },
      null as CashPricePharmacySuggestion | null,
    );

  const preferredPharmacy = cashPricePharmacySuggestions.find((suggestion) => suggestion.is_preferred_pharmacy) ?? null;

  // Make sure the preferred pharmacy is different from the cheapest pharmacy
  if (preferredPharmacy && preferredPharmacy.pharmacy_id === cheapestPharmacy?.pharmacy_id) {
    return { cheapestPharmacy, preferredPharmacy: null };
  }

  return { cheapestPharmacy, preferredPharmacy };
};
